
/**
 * Module dependencies.
 */

import { ChangeEvent, KeyboardEvent, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { FieldAlert } from './forms/field-alert';
import { FormControl, FormControlWrapper, FormGroup } from './forms';
import { IconButton } from './buttons/icon-button';
import { color, media } from 'react-components/styles';
import { ifProp, theme } from 'styled-tools';
import { isEmpty } from 'lodash';
import { useDebounceCallback } from '@react-hook/debounce';
import { useTranslate } from '../i18n';
import closeIcon24 from './assets/svg/24/close.svg';
import searchIcon24 from './assets/svg/24/search.svg';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  'aria-describedby'?: string;
  className?: string;
  disabled?: boolean;
  helpText?: string;
  id?: string;
  label?: string;
  onSearch: (value: string) => Promise<void>;
  placeholder: string;
};

/**
 * `StyledFormControlWrapper` styled component.
 */

const StyledFormControlWrapper = styled(FormControlWrapper)<{ disabled: boolean; isAlertVisible: boolean }>`
  padding-right: 48px;

  ${ifProp('isAlertVisible', css`
    border-color: ${color('warning500')};
  `)}

  ${ifProp('disabled', css`
    background-color: ${color('gray300')};
    border-color: ${color('gray300')};
  `)}

  * {
    outline: none;
  }

  &:hover {
    ${ifProp('isAlertVisible', css`
      border-color: ${color('warning500')};
    `)}
  }
`;

/**
 * `StyledFormControl` styled component.
 */

const StyledFormControl = styled(FormControl)`
  padding: 7px 0 7px 14px;

  ${media.min('md')`
    padding: 7px 0 7px 14px;
  `}

  &::placeholder {
    color: ${color('gray500')};
    transition: color ${theme('animations.defaultTransition')};
  }

  &:disabled::placeholder {
    color: ${color('gray400')};
  }
`;

/**
 * `StyledIconButton` component.
 */

const StyledIconButton = styled(IconButton)`
  color: ${color('gray500')};
  position: absolute;
  right: 6px;
  top: 2px;

  &:focus,
  &:hover {
    color: ${color('gray900')};
  }

  &:disabled {
    color: ${color('gray400')};
  }
`;

/**
 * Export `SearchInput` component.
 */

export function SearchInput(props: Props): JSX.Element {
  const { className, disabled = false, helpText, id, label, onSearch, placeholder, ...rest } = props;
  const { translate } = useTranslate();
  const [hasFocus, setHasFocus] = useState(false);
  const [isAlertVisible, setAlertVisible] = useState(false);
  const [value, setValue] = useState('');
  const [search, setSearch] = useState('');
  const onSearchRef = useRef(onSearch);
  const hasSearch = !isEmpty(value);
  const handleDebouncedSearch = useDebounceCallback(
    useCallback((search: string) => {
      setSearch(search.trim());
    }, []), 500);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    setValue(inputValue);
    handleDebouncedSearch(inputValue);
  }, [handleDebouncedSearch]);

  const handleKeyUp = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setSearch((event.target as HTMLInputElement).value.trim());
    }
  }, []);

  const handleSearch = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    if (hasSearch) {
      event.preventDefault();
      setValue('');
      setSearch('');

      return;
    }

    return setAlertVisible(true);
  }, [hasSearch]);

  useEffect(() => {
    onSearchRef.current = onSearch;
  });

  useEffect(() => {
    if (onSearchRef.current) {
      onSearchRef.current(search);
    }
  }, [search]);

  return (
    <FormGroup
      disabled={disabled}
      hasAlert={isAlertVisible}
    >
      <StyledFormControlWrapper
        className={className}
        disabled={disabled}
        hasError={false}
        hasFocus={hasFocus}
        isAlertVisible={isAlertVisible}
      >
        <StyledFormControl
          {...rest}
          aria-label={label ?? translate('common.actions.search')}
          disabled={disabled}
          id={id ?? translate('common.actions.search')}
          onBlur={() => {
            setAlertVisible(false);
            setHasFocus(false);
          }}
          onChange={handleChange}
          onFocus={() => {
            setAlertVisible(false);
            setHasFocus(true);
          }}
          onKeyUp={handleKeyUp}
          placeholder={placeholder}
          value={value}
        />
      </StyledFormControlWrapper>

      <StyledIconButton
        aria-label={translate(`common.actions.${hasSearch ? 'clear' : 'search'}`)}
        colorTheme={'transparent'}
        disabled={disabled}
        icon={hasSearch ? closeIcon24 : searchIcon24}
        iconSize={'24px'}
        onClick={handleSearch}
      />

      {helpText && (
        <FieldAlert isOpen={!isAlertVisible}>
          {helpText}
        </FieldAlert>
      )}

      <FieldAlert
        isOpen={isAlertVisible}
        type={'warning'}
      >
        {translate('searchInput.required')}
      </FieldAlert>
    </FormGroup>
  );
}
